import '@hotwired/turbo-rails'
import './controllers'

import { disconnectStreamSource } from '@hotwired/turbo'

/**
 * TURBO ACTIONCABLE IDLE TIMEOUT HANDLER
 *
 * PROBLEM SOLVED:
 * ActionCable connections were staying open for 30+ minutes, causing periodic CPU spikes
 * every 30-60 minutes due to expensive Redis cleanup operations. Rails/Turbo doesn't
 * provide built-in connection timeout configuration.
 *
 * SOLUTION:
 * This automatically disconnects Turbo Stream ActionCable connections after 5 minutes
 * of user inactivity. Turbo will automatically reconnect when needed (page navigation,
 * user returns to tab, etc.).
 *
 * TECHNICAL DETAILS:
 * - Only runs on pages that have Turbo Stream sources (turbo_stream_from helper)
 * - Tracks user activity: clicks, mouse movement, keyboard, scroll, touch
 * - Resets timer when user switches back to tab (visibility change)
 * - Uses Turbo's official disconnectStreamSource API
 * - Runs after both initial page load and Turbo navigation
 *
 * IMPACT:
 * - Prevents long-lived ActionCable connections (>5min becomes rare)
 * - Eliminates periodic Redis cleanup CPU spikes
 * - Maintains user experience (auto-reconnects when needed)
 * - Improves server resource usage
 *
 * MAINTENANCE:
 * - Adjust IDLE_TIMEOUT_MS to change inactivity threshold
 * - Monitor console logs to verify connections are being closed
 * - Check CloudWatch for reduced ActionCable connection durations
 *
 * @see https://turbo.hotwired.dev/handbook/streams
 */

function initTurboIdleTimeout() {
  const IDLE_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes
  const streamSources = document.querySelectorAll('turbo-cable-stream-source')

  // Only run if we have Turbo stream sources (pages using turbo_stream_from)
  if (streamSources.length === 0) {
    return
  }

  console.log(`🔌 Found ${streamSources.length} Turbo stream source(s), setting up ${IDLE_TIMEOUT_MS / 1000 / 60}min idle timeout`)

  let idleTimer

  /**
   * Reset the inactivity timer
   * Called whenever user activity is detected
   */
  function resetTimer() {
    clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      // Get current sources (may have changed since page load)
      const currentSources = document.querySelectorAll('turbo-cable-stream-source')

      if (currentSources.length > 0) {
        currentSources.forEach((source) => {
          disconnectStreamSource(source)
        })
        console.log(`⏰ Disconnected ${currentSources.length} Turbo stream source(s) after ${IDLE_TIMEOUT_MS / 1000 / 60}min inactivity`)
      }
    }, IDLE_TIMEOUT_MS)
  }

  // Activity events that indicate user is actively using the page
  const activityEvents = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart']

  activityEvents.forEach(event =>
    document.addEventListener(event, resetTimer, { passive: true }),
  )

  // Handle user switching back to tab (browser tab became visible again)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      resetTimer() // Reset timer when user returns to tab
    }
  })

  // Start the idle timer immediately
  resetTimer()

  console.log(`✅ Turbo idle timeout initialized - will disconnect after ${IDLE_TIMEOUT_MS / 1000 / 60}min inactivity`)
}

// Initialize on initial page load
document.addEventListener('DOMContentLoaded', initTurboIdleTimeout)

// Prevent duplicate modal windows
window.ModalManager = {
  openModals: new Set(),
  pendingOperations: new Map(),

  isOpen(modalName) {
    return this.openModals.has(modalName)
  },

  canOpen(modalName) {
    return !this.isOpen(modalName) && !this.pendingOperations.has(`open-${modalName}`)
  },

  startOperation(operation, modalName) {
    const key = `${operation}-${modalName}`
    if (this.pendingOperations.has(key)) {
      return false // Operation already in progress
    }
    this.pendingOperations.set(key, Date.now())

    // Auto-cleanup after timeout to prevent stuck states
    setTimeout(() => {
      this.pendingOperations.delete(key)
    }, 1000)

    return true
  },

  finishOperation(operation, modalName) {
    this.pendingOperations.delete(`${operation}-${modalName}`)
  },

  markAsOpen(modalName) {
    this.openModals.add(modalName)
  },

  markAsClosed(modalName) {
    this.openModals.delete(modalName)
  },
}
